/**
 * Learn Intervals Page
 *
 * Comprehensive guide to understanding and training musical intervals.
 * Covers theory, practical applications, and how to use the Intervals Drill tool.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    LearnPageLayout,
    ContentSection,
    CalloutBox,
    OutcomeItem,
} from "../../components/learn";
import { ROUTES } from "../../config/routes";
import { SEO_METADATA } from "../../config/seoMetadata";
import SEO from "../../components/SEO";
import styles from "./StartHere.module.css";

const tocItems = [
    { id: "what-are-intervals", label: "What are intervals?" },
    { id: "why-intervals-matter", label: "Why intervals matter" },
    { id: "interval-types", label: "Types of intervals" },
    { id: "interval-qualities", label: "Interval qualities" },
    { id: "recognizing-intervals", label: "Recognizing intervals" },
    { id: "using-the-drill", label: "Using the intervals drill" },
    { id: "practice-strategies", label: "Practice strategies" },
];

export default function IntervalsLearn() {
    return (
        <>
            <SEO {...SEO_METADATA.LEARN_INTERVALS} />
            <LearnPageLayout
                title="Learn Intervals"
                subtitle="Master the building blocks of melody and harmony"
                tocItems={tocItems}
            >
            {/* What are Intervals */}
            <ContentSection id="what-are-intervals">
                <h2 className={styles.sectionTitle}>What are intervals?</h2>

                <p>
                    An <strong>interval</strong> is simply the distance between two notes. That's
                    it. When you play two notes together or one after another, the space between
                    them is an interval.
                </p>

                <p>
                    Think about it: when you hear the first two notes of "Happy Birthday," you're
                    hearing an interval. When you hear the opening of "Somewhere Over the
                    Rainbow," that's an interval too (a big one—an octave!).
                </p>

                <CalloutBox>
                    <p>
                        <strong>Key insight:</strong> Intervals are about relationships, not
                        absolute notes. The distance from C to E is the same interval as the
                        distance from G to B—both are Major 3rds, even though the notes are
                        different.
                    </p>
                </CalloutBox>

                <p>
                    Intervals are measured in <strong>semitones</strong> (also called half steps).
                    On a piano, a semitone is the distance from any key to the very next key,
                    including black keys. For example, C to C# is one semitone, and E to F is also
                    one semitone (there's no black key between them).
                </p>
            </ContentSection>

            {/* Why Intervals Matter */}
            <ContentSection id="why-intervals-matter">
                <h2 className={styles.sectionTitle}>Why intervals matter</h2>

                <p>
                    Intervals are the DNA of music. Every melody, chord, and harmony is built from
                    them. When you can recognize intervals by ear, you unlock several powerful
                    abilities:
                </p>

                <div style={{ marginTop: "1.5rem" }}>
                    <OutcomeItem icon="🎵" title="Play melodies by ear">
                        Hear a song and immediately know which intervals to play, without trial and error.
                    </OutcomeItem>
                    <OutcomeItem icon="🎸" title="Transcribe music accurately">
                        Listen to a recording and write down or play back exactly what you hear.
                    </OutcomeItem>
                    <OutcomeItem icon="🎹" title="Improvise with confidence">
                        Know which intervals will sound good before you play them—hear it in your head first.
                    </OutcomeItem>
                    <OutcomeItem icon="🎼" title="Communicate with other musicians">
                        Tell your bandmate 'go up a major 6th' and they'll know exactly what you mean.
                    </OutcomeItem>
                    <OutcomeItem icon="✍️" title="Write better melodies">
                        Understand which intervals create tension, resolution, or emotion in your compositions.
                    </OutcomeItem>
                </div>
            </ContentSection>

            {/* Types of Intervals */}
            <ContentSection id="interval-types">
                <h2 className={styles.sectionTitle}>Types of intervals</h2>

                <p>
                    There are 13 common intervals, ranging from 0 semitones (a unison—playing the
                    same note) to 12 semitones (an octave—the same note one "letter" higher or
                    lower). Here's the complete list:
                </p>

                <div className={styles.intervalTable}>
                    <table style={{ width: "100%", marginTop: "1rem", borderCollapse: "collapse" }}>
                        <thead>
                            <tr style={{ borderBottom: "2px solid var(--text-color)", textAlign: "left" }}>
                                <th style={{ padding: "0.75rem" }}>Interval Name</th>
                                <th style={{ padding: "0.75rem" }}>Semitones</th>
                                <th style={{ padding: "0.75rem" }}>Example (from C)</th>
                                <th style={{ padding: "0.75rem" }}>Famous Song Reference</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Unison</strong></td>
                                <td style={{ padding: "0.75rem" }}>0</td>
                                <td style={{ padding: "0.75rem" }}>C → C</td>
                                <td style={{ padding: "0.75rem" }}>Same note</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Minor 2nd</strong></td>
                                <td style={{ padding: "0.75rem" }}>1</td>
                                <td style={{ padding: "0.75rem" }}>C → C♯/D♭</td>
                                <td style={{ padding: "0.75rem" }}>"Jaws" theme</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Major 2nd</strong></td>
                                <td style={{ padding: "0.75rem" }}>2</td>
                                <td style={{ padding: "0.75rem" }}>C → D</td>
                                <td style={{ padding: "0.75rem" }}>"Happy Birthday"</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Minor 3rd</strong></td>
                                <td style={{ padding: "0.75rem" }}>3</td>
                                <td style={{ padding: "0.75rem" }}>C → E♭</td>
                                <td style={{ padding: "0.75rem" }}>"Greensleeves"</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Major 3rd</strong></td>
                                <td style={{ padding: "0.75rem" }}>4</td>
                                <td style={{ padding: "0.75rem" }}>C → E</td>
                                <td style={{ padding: "0.75rem" }}>"When the Saints Go Marching In"</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Perfect 4th</strong></td>
                                <td style={{ padding: "0.75rem" }}>5</td>
                                <td style={{ padding: "0.75rem" }}>C → F</td>
                                <td style={{ padding: "0.75rem" }}>"Here Comes the Bride"</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Tritone</strong></td>
                                <td style={{ padding: "0.75rem" }}>6</td>
                                <td style={{ padding: "0.75rem" }}>C → F♯/G♭</td>
                                <td style={{ padding: "0.75rem" }}>"The Simpsons" theme</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Perfect 5th</strong></td>
                                <td style={{ padding: "0.75rem" }}>7</td>
                                <td style={{ padding: "0.75rem" }}>C → G</td>
                                <td style={{ padding: "0.75rem" }}>"Star Wars" theme</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Minor 6th</strong></td>
                                <td style={{ padding: "0.75rem" }}>8</td>
                                <td style={{ padding: "0.75rem" }}>C → A♭</td>
                                <td style={{ padding: "0.75rem" }}>"The Entertainer"</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Major 6th</strong></td>
                                <td style={{ padding: "0.75rem" }}>9</td>
                                <td style={{ padding: "0.75rem" }}>C → A</td>
                                <td style={{ padding: "0.75rem" }}>"My Bonnie Lies Over the Ocean"</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Minor 7th</strong></td>
                                <td style={{ padding: "0.75rem" }}>10</td>
                                <td style={{ padding: "0.75rem" }}>C → B♭</td>
                                <td style={{ padding: "0.75rem" }}>"Somewhere" (West Side Story)</td>
                            </tr>
                            <tr style={{ borderBottom: "1px solid var(--border-color)" }}>
                                <td style={{ padding: "0.75rem" }}><strong>Major 7th</strong></td>
                                <td style={{ padding: "0.75rem" }}>11</td>
                                <td style={{ padding: "0.75rem" }}>C → B</td>
                                <td style={{ padding: "0.75rem" }}>"Take On Me"</td>
                            </tr>
                            <tr>
                                <td style={{ padding: "0.75rem" }}><strong>Octave</strong></td>
                                <td style={{ padding: "0.75rem" }}>12</td>
                                <td style={{ padding: "0.75rem" }}>C → C (higher)</td>
                                <td style={{ padding: "0.75rem" }}>"Somewhere Over the Rainbow"</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </ContentSection>

            {/* Interval Qualities */}
            <ContentSection id="interval-qualities">
                <h2 className={styles.sectionTitle}>Understanding interval qualities</h2>

                <p>
                    You might have noticed that intervals have names like "major," "minor," and
                    "perfect." These are called <strong>qualities</strong>, and they describe the
                    character or color of the interval.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Perfect intervals</h3>
                <p>
                    <strong>Perfect 4th, Perfect 5th, and Octave</strong> are called "perfect"
                    because they sound stable and consonant. They're the same in both major and
                    minor keys. These intervals have been considered "perfect" since medieval times
                    because of their pure, simple sound relationships.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Major and minor intervals</h3>
                <p>
                    <strong>2nds, 3rds, 6ths, and 7ths</strong> come in major and minor versions.
                    The major version is one semitone larger than the minor version.
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Major intervals</strong> tend to sound bright, happy, and open
                    </li>
                    <li>
                        <strong>Minor intervals</strong> tend to sound darker, sadder, or more tense
                    </li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>The tritone</h3>
                <p>
                    The <strong>tritone</strong> (6 semitones) is special. It's the most dissonant
                    interval—it sounds unstable and wants to resolve. In medieval times, it was
                    called "diabolus in musica" (the devil in music). It's essential in jazz and
                    blues, and it's what makes dominant 7th chords want to resolve to the tonic.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Pro tip:</strong> Don't worry too much about memorizing the theory
                        at first. Focus on training your ears to recognize the <em>sound</em> of
                        each interval. The theory will make more sense as you hear the differences.
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Recognizing Intervals */}
            <ContentSection id="recognizing-intervals">
                <h2 className={styles.sectionTitle}>How to recognize intervals</h2>

                <p>
                    The key to recognizing intervals is creating mental reference points. Here are
                    the most effective strategies:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Use song references</h3>
                <p>
                    The famous song examples in the table above are powerful memory aids. When you
                    hear an interval in the wild, you can think "that sounds like the beginning of
                    Happy Birthday—it's a Major 2nd!"
                </p>
                <p>
                    Even better: find your own song references. Use songs you know really well and
                    love. The more personal the connection, the easier it will stick.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Understand the character</h3>
                <p>Each interval has a distinct emotional quality:</p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li><strong>Minor 2nd:</strong> Very tense, creepy, unsettling</li>
                    <li><strong>Major 2nd:</strong> Neutral, stepwise, comfortable</li>
                    <li><strong>Minor 3rd:</strong> Sad, melancholic, introspective</li>
                    <li><strong>Major 3rd:</strong> Happy, bright, cheerful</li>
                    <li><strong>Perfect 4th:</strong> Stable, strong, anthem-like</li>
                    <li><strong>Tritone:</strong> Unstable, mysterious, jazzy</li>
                    <li><strong>Perfect 5th:</strong> Very stable, powerful, open</li>
                    <li><strong>Minor 6th:</strong> Plaintive, yearning</li>
                    <li><strong>Major 6th:</strong> Expansive, romantic, soaring</li>
                    <li><strong>Minor 7th:</strong> Bluesy, laid-back, funky</li>
                    <li><strong>Major 7th:</strong> Dreamy, dissonant, modern</li>
                    <li><strong>Octave:</strong> Same note, just higher/lower</li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Practice with context</h3>
                <p>
                    Intervals can be played in two ways:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Melodic:</strong> Notes played one after another (like in a melody)
                    </li>
                    <li>
                        <strong>Harmonic:</strong> Both notes played at the same time (like in a
                        chord)
                    </li>
                </ul>
                <p>
                    They sound different! A melodic Major 3rd has a direction and motion, while a
                    harmonic Major 3rd has a blended, consonant quality. Practice recognizing both.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Start small and build up</h3>
                <p>
                    Don't try to learn all 13 intervals at once. Start with just 2-3 that sound
                    very different from each other—maybe Minor 3rd, Major 3rd, and Perfect 5th.
                    Once you can reliably identify those, add more.
                </p>
            </ContentSection>

            {/* Using the Drill */}
            <ContentSection id="using-the-drill">
                <h2 className={styles.sectionTitle}>Using the intervals drill</h2>

                <p>
                    Now that you understand the theory, it's time to train your ears! The{" "}
                    <Link to={ROUTES.DRILL.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                        Intervals Drill
                    </Link>{" "}
                    is designed to help you recognize intervals quickly and accurately.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>How the drill works</h3>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Choose your intervals:</strong> Select which intervals you want to
                        practice. Start with just 2-3, then add more as you improve.
                    </li>
                    <li>
                        <strong>Select a mode:</strong>
                        <ul style={{ marginLeft: "1.5rem", marginTop: "0.5rem" }}>
                            <li>
                                <strong>Questions mode:</strong> Practice a set number of questions
                                (e.g., 10 questions)
                            </li>
                            <li>
                                <strong>Time mode:</strong> Practice for a set duration (e.g., 60
                                seconds)
                            </li>
                            <li>
                                <strong>Endless mode:</strong> Keep practicing with no limit—great
                                for deep practice sessions
                            </li>
                        </ul>
                    </li>
                    <li>
                        <strong>Start the drill:</strong> You'll hear two notes (either melodic or
                        harmonic—you can change this in Playback Options).
                    </li>
                    <li>
                        <strong>Identify the interval:</strong> Select which interval you heard from
                        the available options.
                    </li>
                    <li>
                        <strong>Get instant feedback:</strong> The drill tells you immediately if
                        you're correct. If not, you can try again on the same question.
                    </li>
                    <li>
                        <strong>Review your results:</strong> At the end, see your score and track
                        your progress.
                    </li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Playback options</h3>
                <p>
                    The drill offers several playback settings to customize your practice:
                </p>
                <ul style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>
                        <strong>Instrument:</strong> Choose from piano, guitar, strings, winds, and
                        more. Different timbres can help you hear intervals in different contexts.
                    </li>
                    <li>
                        <strong>Play mode:</strong> Switch between melodic (sequential) and harmonic
                        (simultaneous) intervals.
                    </li>
                    <li>
                        <strong>Volume:</strong> Adjust to your preference.
                    </li>
                    <li>
                        <strong>Replay button:</strong> Hear the interval as many times as you need
                        before answering.
                    </li>
                </ul>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>Visual aid: Piano keyboard</h3>
                <p>
                    You can toggle a piano keyboard display that shows you the two notes being
                    played. This is helpful when you're learning, as you can see the physical
                    distance between the notes. As you get better, try practicing without the
                    visual aid to rely purely on your ears.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Ready to start?</strong> Head to the{" "}
                        <Link to={ROUTES.DRILL.INTERVALS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Intervals Drill
                        </Link>{" "}
                        and begin training your ears!
                    </p>
                </CalloutBox>
            </ContentSection>

            {/* Practice Strategies */}
            <ContentSection id="practice-strategies">
                <h2 className={styles.sectionTitle}>Effective practice strategies</h2>

                <p>
                    Like any skill, interval recognition improves with consistent, focused
                    practice. Here are strategies to accelerate your progress:
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>1. Practice daily, even if brief</h3>
                <p>
                    Ten minutes every day is far more effective than one hour once a week. Your
                    brain needs regular exposure to build pattern recognition. Use the Time mode
                    set to 5-10 minutes as a daily warm-up.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>2. Start simple, then add complexity</h3>
                <p>Follow this progression:</p>
                <ol style={{ marginLeft: "1.5rem", lineHeight: "1.8" }}>
                    <li>Start with 2 very different intervals (e.g., Minor 3rd and Perfect 5th)</li>
                    <li>Add a third interval once you're 80%+ accurate</li>
                    <li>Gradually include all intervals</li>
                    <li>Practice both melodic and harmonic playback</li>
                    <li>Try different instruments to hear intervals in new contexts</li>
                </ol>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>3. Focus on your weak spots</h3>
                <p>
                    If you keep confusing Major 3rds and Perfect 4ths, create a drill with just
                    those two intervals until you can reliably tell them apart. Targeted practice
                    is powerful.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>4. Sing the intervals</h3>
                <p>
                    After you identify an interval correctly, try singing it yourself. This
                    reinforces the connection between what you hear and what you can produce. It
                    also helps with pitch matching and intonation.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>5. Apply it to real music</h3>
                <p>
                    Listen to songs you love and try to identify the intervals in the melodies.
                    When you hear a catchy riff or melody, ask yourself: "What intervals am I
                    hearing?" This connects your practice to real-world music.
                </p>

                <h3 style={{ marginTop: "2rem", marginBottom: "1rem" }}>6. Track your progress</h3>
                <p>
                    Keep notes on your scores and which intervals give you trouble. Celebrate
                    improvements! Going from 60% to 80% accuracy is a huge accomplishment.
                </p>

                <CalloutBox>
                    <p>
                        <strong>Remember:</strong> Everyone progresses at their own pace. Some
                        intervals will click immediately, others will take time. Stay patient and
                        consistent, and you'll be amazed at how quickly your ears develop.
                    </p>
                </CalloutBox>

                <div style={{ marginTop: "3rem", padding: "1.5rem", background: "var(--callout-bg)", borderRadius: "8px" }}>
                    <h3 style={{ marginTop: 0 }}>Next Steps</h3>
                    <p style={{ marginBottom: "1rem" }}>
                        Now that you understand intervals, you're ready to practice:
                    </p>
                    <Link
                        to={ROUTES.DRILL.INTERVALS}
                        style={{
                            display: "inline-block",
                            padding: "0.75rem 1.5rem",
                            background: "var(--accent-color)",
                            color: "white",
                            borderRadius: "6px",
                            textDecoration: "none",
                            fontWeight: "600",
                            marginRight: "1rem",
                        }}
                    >
                        Start Intervals Drill →
                    </Link>
                    <p style={{ marginTop: "1.5rem", marginBottom: 0 }}>
                        Or continue learning about{" "}
                        <Link to={ROUTES.LEARN.CHORDS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Chords
                        </Link>{" "}
                        and{" "}
                        <Link to={ROUTES.LEARN.PROGRESSIONS} style={{ color: "var(--accent-color)", textDecoration: "underline" }}>
                            Progressions
                        </Link>
                        .
                    </p>
                </div>
            </ContentSection>
        </LearnPageLayout>
        </>
    );
}
